/**
 * §28 pricing-guardrail (NT-SEG-3). Makes an architectural invariant executable:
 * account-intelligence facets must NEVER influence price resolution.
 *
 * Pricing resolves from item + quantity + effective date only (ADR-0004). The
 * §28 account facets — AccountSegment (versioned tier), Vertical, Potential,
 * FundingSignal — drive sales-motion / posture / sourcing-resilience weighting,
 * and MUST NOT reach price resolution (§15) or attach to a Sourceable (§4).
 * Consequence: two accounts identical except for a §28 facet resolve to the
 * SAME price for the same item/qty/date.
 *
 * Sources: security review C30 (gate3-foundation-security-review.md §6);
 * selling-system-implementation-spec.md §28 "Account facet".
 *
 * This module is the single source of truth for the forbidden vocabulary. The
 * future pricing-resolve entrypoint MUST adopt {@link RejectsAccountFacets} on
 * its input type and reject any {@link accountFacetKeysIn} hit at runtime — so
 * the boundary is enforced at compile time AND at run time and cannot regress.
 */

/**
 * The §28 account-intelligence facet names forbidden as price-resolution input.
 * Includes canonical facet names and their common shorthand (`segment`, `tier`).
 */
export const ACCOUNT_FACET_KEYS = [
  'accountSegment',
  'segment',
  'tier',
  'vertical',
  'potential',
  'fundingSignal',
  'accountIntelligence',
] as const

export type AccountFacetKey = (typeof ACCOUNT_FACET_KEYS)[number]

const FACET_LOOKUP = new Set<string>(ACCOUNT_FACET_KEYS.map((key) => key.toLowerCase()))

/** True iff `key` names a §28 account-intelligence facet (case-insensitive). */
export function isAccountFacetKey(key: string): boolean {
  return FACET_LOOKUP.has(key.toLowerCase())
}

/**
 * The §28 account-facet keys present on `input` (case-insensitive), empty when
 * none. A pricing-resolve entrypoint MUST reject any non-empty result: its
 * input may not be segmentable by an account facet (NT-SEG-3).
 */
export function accountFacetKeysIn(input: object): string[] {
  return Object.keys(input).filter(isAccountFacetKey)
}

/**
 * Type-level guard: resolves to `T` when `T` carries NO account-facet key, and
 * to `never` otherwise — so a pricing-resolve input type that grows a facet
 * fails to typecheck. Wrap the future resolver's input type in it:
 *
 *   function resolvePrice(input: RejectsAccountFacets<PriceResolutionInput>): Price
 */
export type RejectsAccountFacets<T> = Extract<keyof T, AccountFacetKey> extends never ? T : never
